import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import { API_BASE_URL } from '../../config/config';

/**
 * EditOverrideModal
 * Modal for editing manual overrides for baggage (arrivals) and gate (departures)
 */
const EditOverrideModal = ({ open, onClose, flight, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    baggage: '',
    baggageExpiry: '',
    gate: '',
    gateExpiry: ''
  });

  // Populate form when flight changes
  useEffect(() => {
    if (flight) {
      setFormData({
        baggage: flight.arrivalBaggageOverride?.value || '',
        baggageExpiry: flight.arrivalBaggageOverride?.expiresAt
          ? new Date(flight.arrivalBaggageOverride.expiresAt).toISOString().slice(0, 16)
          : '',
        gate: flight.departureGateOverride?.value || '',
        gateExpiry: flight.departureGateOverride?.expiresAt
          ? new Date(flight.departureGateOverride.expiresAt).toISOString().slice(0, 16)
          : ''
      });
      setError('');
    }
  }, [flight]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSave = async () => {
    if (!flight) return;

    setLoading(true);
    setError('');

    try {
      // Get auth token from localStorage
      const authData = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = authData.token;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Build request body based on flight direction
      const requestBody = {};

      if (flight.direction === 'A') {
        // Arrival flight - send baggage override
        requestBody.arrivalBaggage = {
          value: formData.baggage.trim() || null,
          expiresAt: formData.baggageExpiry || null
        };
      } else if (flight.direction === 'D') {
        // Departure flight - send gate override
        requestBody.departureGate = {
          value: formData.gate.trim() || null,
          expiresAt: formData.gateExpiry || null
        };
      }


      // Make API request
      const response = await fetch(`${API_BASE_URL}/api/flights/${flight._id}/overrides`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to update override: ${response.status}`);
      }

      // Success - notify parent component
      if (onSuccess) {
        onSuccess(data.data);
      }

      // Close modal
      onClose();

    } catch (err) {
      console.error('Error updating override:', err);
      setError(err.message || 'Failed to update override. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!flight) return;

    // Clear the relevant field based on direction
    if (flight.direction === 'A') {
      setFormData(prev => ({ ...prev, baggage: '', baggageExpiry: '' }));
    } else if (flight.direction === 'D') {
      setFormData(prev => ({ ...prev, gate: '', gateExpiry: '' }));
    }
  };

  if (!flight) return null;

  const isArrival = flight.direction === 'A';
  const isDeparture = flight.direction === 'D';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#fff', color: '#000' }
      }}
    >
      <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        Edit Manual Override
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Flight Summary */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Flight Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Flight:</strong> {flight.airline}{flight.flightNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Type:</strong>{' '}
                <Chip
                  label={isArrival ? 'Arrival' : 'Departure'}
                  size="small"
                  color={isArrival ? 'primary' : 'secondary'}
                  sx={{ ml: 0.5 }}
                />
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Scheduled:</strong>{' '}
                {flight.scheduledTime
                  ? new Date(flight.scheduledTime).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>{isArrival ? 'Origin' : 'Destination'}:</strong>{' '}
                {isArrival ? flight.route?.origin || 'N/A' : flight.route?.destination || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Arrival Baggage Override (only for arrivals) */}
        {isArrival && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Baggage / Carousel Override
            </Typography>
            <TextField
              fullWidth
              label="Baggage / Carousel"
              placeholder="e.g., Belt 3, Carousel A2"
              value={formData.baggage}
              onChange={(e) => handleInputChange('baggage', e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="Leave empty to clear manual override"
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Expires At (Optional)"
              value={formData.baggageExpiry}
              onChange={(e) => handleInputChange('baggageExpiry', e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
          </Box>
        )}

        {/* Departure Gate Override (only for departures) */}
        {isDeparture && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Gate Override
            </Typography>
            <TextField
              fullWidth
              label="Gate"
              placeholder="e.g., Gate B12, Gate C3"
              value={formData.gate}
              onChange={(e) => handleInputChange('gate', e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText="Leave empty to clear manual override"
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Expires At (Optional)"
              value={formData.gateExpiry}
              onChange={(e) => handleInputChange('gateExpiry', e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleClear}
          disabled={loading}
          color="warning"
          variant="outlined"
        >
          Clear Override
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditOverrideModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  flight: PropTypes.object,
  onSuccess: PropTypes.func
};

export default EditOverrideModal;
